<template>
  <div class="flex-1 overflow-auto p-6">
    <!-- QM §1.2 — see Index.vue: the route is reachable by URL regardless of the sidebar. -->
    <p v-if="!isQueueManagementEnabled" class="text-slate-600">
      {{ $t('QUEUES_MGMT.DISABLED') }}
    </p>
    <div v-else class="max-w-[75rem]">
      <h1 class="text-2xl font-medium text-slate-900 dark:text-slate-100 mb-1">
        {{ $t('QUEUES_MGMT.SUPERVISOR.HEADER') }}
      </h1>
      <p class="text-sm text-slate-600 dark:text-slate-400 mb-4">
        {{ $t('QUEUES_MGMT.SUPERVISOR.SUB_TEXT') }}
      </p>

      <queues-nav current="queues_supervisor" />

      <p v-if="!hasQueueSupervision" class="mt-6 text-slate-600">
        {{ $t('QUEUES_MGMT.SUPERVISOR.FORBIDDEN') }}
      </p>

      <template v-else>
        <!-- QM §5.7 — tiles are push-and-poll driven; say so when the socket drops -->
        <queue-connection-banner
          :is-refreshing="isLoadingOverview"
          @refresh="loadOverview"
        />

        <div class="flex items-center justify-between mt-4 mb-3">
          <span class="text-xs text-slate-500 dark:text-slate-400">
            {{ $t('QUEUES_MGMT.SUPERVISOR.AUTO_REFRESH') }}
          </span>
          <woot-button
            variant="smooth"
            size="tiny"
            icon="arrow-clockwise"
            :is-loading="isLoadingOverview"
            @click="loadOverview"
          >
            {{ $t('QUEUES_MGMT.SUPERVISOR.REFRESH') }}
          </woot-button>
        </div>

        <woot-loading-state
          v-if="isLoadingOverview && !tiles.length"
          :message="$t('QUEUES_MGMT.SUPERVISOR.LOADING')"
        />

        <p v-else-if="!tiles.length" class="text-slate-600 dark:text-slate-400">
          {{ $t('QUEUES_MGMT.SUPERVISOR.EMPTY') }}
        </p>

        <!-- QM §5.5.1 — per-queue tiles -->
        <div v-else class="grid grid-cols-1 md:grid-cols-3 gap-3">
          <button
            v-for="tile in tiles"
            :key="tile.id"
            class="text-start p-4 rounded-lg border bg-white dark:bg-slate-900 cursor-pointer hover:border-woot-500 hover:shadow-sm transition-colors"
            :class="
              tile.id === selectedQueueId
                ? 'border-woot-500'
                : 'border-slate-100 dark:border-slate-800'
            "
            @click="selectQueue(tile.id)"
          >
            <p class="font-medium text-slate-900 dark:text-slate-100 mb-2">
              {{ tile.name }}
            </p>
            <p class="text-3xl font-medium text-slate-900 dark:text-slate-100">
              {{ tile.unassigned_count }}
            </p>
            <p class="text-xs text-slate-500 dark:text-slate-400 mb-2">
              {{ $t('QUEUES_MGMT.SUPERVISOR.TILE.UNASSIGNED') }}
            </p>
            <dl class="text-xs text-slate-600 dark:text-slate-400 grid gap-0.5">
              <div class="flex justify-between">
                <dt>{{ $t('QUEUES_MGMT.SUPERVISOR.TILE.OLDEST') }}</dt>
                <dd>{{ relativeTime(tile.oldest_waiting_since) }}</dd>
              </div>
              <!-- QM §5.5.1 — SLA-at-risk against the account's configured threshold -->
              <div class="flex justify-between">
                <dt>
                  {{
                    $t('QUEUES_MGMT.SUPERVISOR.TILE.SLA_AT_RISK', {
                      percent: tile.sla_risk_threshold,
                    })
                  }}
                </dt>
                <dd
                  :class="{
                    'text-red-600 dark:text-red-400':
                      tile.sla_at_risk_count > 0,
                  }"
                >
                  {{ tile.sla_at_risk_count }}
                </dd>
              </div>
              <div class="flex justify-between">
                <dt>{{ $t('QUEUES_MGMT.SUPERVISOR.TILE.EXPANDED') }}</dt>
                <dd>{{ tile.expanded_count }}</dd>
              </div>
              <div class="flex justify-between">
                <dt>{{ $t('QUEUES_MGMT.SUPERVISOR.TILE.ASSIGNED') }}</dt>
                <dd>{{ tile.assigned_count }}</dd>
              </div>
              <div class="flex justify-between">
                <dt>{{ $t('QUEUES_MGMT.SUPERVISOR.TILE.LOCKED') }}</dt>
                <dd>{{ tile.locked_count }}</dd>
              </div>
              <!-- QM §5.5.1 — agent availability across the queue's groups -->
              <div class="flex justify-between">
                <dt>{{ $t('QUEUES_MGMT.SUPERVISOR.TILE.AGENTS') }}</dt>
                <dd>{{ agentStatusLabel(tile) }}</dd>
              </div>
            </dl>
          </button>
        </div>

        <!-- QM §5.5.2 — drill-down: unassigned, assigned and agents tables -->
        <queue-drill-down
          v-if="selectedQueueId"
          :queue-id="selectedQueueId"
          :queue-name="selectedQueueName"
          @changed="loadOverview"
        />
      </template>
    </div>
  </div>
</template>

<script>
import { formatDistanceToNowStrict, parseISO } from 'date-fns';
import alertMixin from 'shared/mixins/alertMixin';
import queueSupervisionMixin from 'dashboard/mixins/queueSupervision';
import QueuesNav from './QueuesNav.vue';
import QueueDrillDown from './QueueDrillDown.vue';
import QueueConnectionBanner from './QueueConnectionBanner.vue';

// Tiles are counters that change without any action of ours (routing, overflow
// expansion, agents pulling), so poll rather than wait for a conversation event.
const REFRESH_INTERVAL = 15000;

export default {
  components: {
    QueuesNav,
    QueueDrillDown,
    QueueConnectionBanner,
  },
  mixins: [alertMixin, queueSupervisionMixin],
  data() {
    return {
      tiles: [],
      selectedQueueId: null,
      isLoadingOverview: false,
      refreshTimer: null,
    };
  },
  computed: {
    selectedQueueName() {
      const tile = this.tiles.find(item => item.id === this.selectedQueueId);
      return tile ? tile.name : '';
    },
  },
  mounted() {
    if (!this.isQueueManagementEnabled || !this.hasQueueSupervision) {
      return;
    }
    // The drill-down modals need the full queue records (routing groups, names).
    this.$store.dispatch('queues/get');
    this.loadOverview();
    this.refreshTimer = setInterval(this.refresh, REFRESH_INTERVAL);
  },
  beforeDestroy() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
    }
  },
  methods: {
    relativeTime(value) {
      if (!value) {
        return '—';
      }
      return formatDistanceToNowStrict(parseISO(value), { addSuffix: true });
    },
    // Spelled out rather than colour-coded, per QM §5.10 (never colour alone).
    agentStatusLabel(tile) {
      return this.$t('QUEUES_MGMT.SUPERVISOR.TILE.AGENTS_VALUE', {
        online: tile.online_count,
        busy: tile.busy_count,
        offline: tile.offline_count,
      });
    },
    async loadOverview() {
      this.isLoadingOverview = true;
      try {
        this.tiles = await this.$store.dispatch('queues/getOverview');
      } catch (error) {
        this.showAlert(this.$t('QUEUES_MGMT.SUPERVISOR.TOAST.ERROR'));
      } finally {
        this.isLoadingOverview = false;
      }
    },
    // Silent tick — no spinner, so an open drill-down does not flicker.
    async refresh() {
      try {
        this.tiles = await this.$store.dispatch('queues/getOverview');
      } catch (error) {
        // A failed background tick is not worth a toast; the next one retries.
      }
    },
    selectQueue(queueId) {
      this.selectedQueueId =
        this.selectedQueueId === queueId ? null : Number(queueId);
    },
  },
};
</script>
